import { ATTENTION_TINT } from "./attention";
import { useId } from "react";
import { cn } from "../../app/components/primitives/utils";

// How the customer pays, the same on the quote and the order page (Ben,
// 5 Oct): the payer's usable methods (credit control's labels), pro-forma
// among them while the sales ledger is on, else shown as not available yet
// with orders' sentence (EA-D5), and why card
// isn't offered when it isn't. On a quote it is a choice; on an order the
// method is settled, so it shows the same list with the chosen one marked.

export type PaymentMethod = "card" | "proforma" | "credit_account";

const HINTS: Record<PaymentMethod, { open: string; settled: string }> = {
  card: {
    open: "pay now, on the next page",
    settled: "paid by card online",
  },
  proforma: {
    open: "we send a pro-forma to pay by bank transfer",
    settled: "paid against a pro-forma",
  },
  credit_account: {
    open: "we invoice your account",
    settled: "we invoice your account",
  },
};

export function PaymentMethodChoice({
  methods,
  value,
  onChange,
  proformaRefusedSentence,
  cardNotAvailableSentence,
  error,
  compact = false,
  stacked = false,
}: {
  methods: Array<{ method: PaymentMethod; label: string }>;
  value: PaymentMethod | null;
  /** Absent: the method is settled (an order), shown read-only. */
  onChange?: (method: PaymentMethod) => void;
  proformaRefusedSentence: string | null;
  cardNotAvailableSentence: string | null;
  error?: string | null;
  /** In the quote's decision summary: a select. */
  compact?: boolean;
  /** With `compact`: the label above a full-width select, for the summary panel and the phone sheet. */
  stacked?: boolean;
}) {
  const name = useId();
  const errorId = `${name}-error`;
  const isSettled = onChange === undefined;

  if (compact) {
    return (
      <label
        className={cn(
          "text-sm",
          stacked
            ? "grid gap-1.5 font-medium"
            : "flex items-center gap-2 text-muted-foreground",
        )}
      >
        Pay by
        <select
          className={cn(
            "rounded-md border bg-background px-2 text-sm font-normal text-foreground",
            stacked ? "h-9 w-full" : "h-8 max-w-32 sm:max-w-none",
            error ? "border-red-500" : value === null && ATTENTION_TINT,
          )}
          value={value ?? ""}
          aria-invalid={error ? true : undefined}
          onChange={(e) => onChange?.(e.target.value as PaymentMethod)}
        >
          <option value="">Choose…</option>
          {methods.map((m) => (
            <option key={m.method} value={m.method}>
              {m.label}
            </option>
          ))}
          {proformaRefusedSentence && (
            <option disabled value="proforma">
              Pro-forma: not available yet
            </option>
          )}
        </select>
      </label>
    );
  }

  return (
    <fieldset
      className="space-y-2"
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? errorId : undefined}
    >
      <legend className="mb-1 text-sm font-medium">
        {isSettled ? "Method" : "How would you like to pay?"}
      </legend>
      {methods.map((m) => (
        <label key={m.method} className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name={name}
            value={m.method}
            checked={value === m.method}
            disabled={isSettled}
            onChange={() => onChange?.(m.method)}
          />
          {m.label}
          <span className="text-xs text-muted-foreground">
            {isSettled ? HINTS[m.method].settled : HINTS[m.method].open}
          </span>
        </label>
      ))}
      {proformaRefusedSentence && (
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <input
            type="radio"
            disabled
            aria-label="Bank transfer (pro-forma)"
            className="mt-1"
          />
          <span>
            Bank transfer (pro-forma)
            <span className="block text-xs">{proformaRefusedSentence}</span>
          </span>
        </div>
      )}
      {cardNotAvailableSentence && (
        <p className="text-xs text-muted-foreground">
          {cardNotAvailableSentence}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="text-sm text-red-700 dark:text-red-400"
        >
          {error}
        </p>
      )}
    </fieldset>
  );
}
